import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ChevronRight, CheckCircle2, DollarSign, Calendar, Users, 
  Shield, Clock, Heart, Star, ArrowRight, Sparkles, 
  MessageCircle, MapPin, Award
} from "lucide-react";
import { Button } from "../components/ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_emergent-website-3/artifacts/3k02iwbx_btr_logo_mockup-removebg-preview.png";
const LOGO_ICON = "https://customer-assets.emergentagent.com/job_emergent-website-3/artifacts/4o9bpmcq_BTR_Health_-_1-removebg-preview.png";

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

export default function BecomeWorkerPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);

  const benefits = [
    {
      icon: DollarSign,
      title: "Earn Great Money",
      description: "Set your own rates and earn $45-$80+ per hour. You're in control of your income.",
      color: "bg-emerald-100 text-emerald-600"
    },
    {
      icon: Calendar,
      title: "Flexible Schedule",
      description: "Work when you want. Choose your own hours and availability. Perfect work-life balance.",
      color: "bg-blue-100 text-blue-600"
    },
    {
      icon: Heart,
      title: "Make a Real Difference",
      description: "Help people live their best lives. Every day you'll see the positive impact you make.",
      color: "bg-pink-100 text-pink-600"
    },
    {
      icon: Shield,
      title: "Full Support & Training",
      description: "We provide ongoing training, insurance coverage, and 24/7 support when you need it.",
      color: "bg-purple-100 text-purple-600"
    }
  ];

  const howItWorks = [
    {
      step: 1,
      title: "Create Your Profile",
      description: "Sign up in 5 minutes. Tell us about your skills, experience, and the services you offer.",
      icon: Users
    },
    {
      step: 2,
      title: "Get Verified",
      description: "Complete a quick background check. We'll verify your qualifications to build trust with participants.",
      icon: Shield
    },
    {
      step: 3,
      title: "Start Receiving Requests",
      description: "Participants in your area will find you and send booking requests. You choose which jobs to accept.",
      icon: MessageCircle
    },
    {
      step: 4,
      title: "Provide Support & Get Paid",
      description: "Deliver amazing support, build relationships, and get paid directly into your account weekly.",
      icon: DollarSign
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_ICON} alt="BTR Health" className="h-8" />
              <span className="font-bold text-xl text-slate-900">BTR Health</span>
            </Link>
            <div className="flex items-center gap-4">
              <Button 
                className="bg-[#87CEEB] hover:bg-[#6BB8DB] text-white rounded-full px-6"
                onClick={() => navigate("/worker-signup")}
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-[#87CEEB]/10 via-white to-emerald-50/30">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div {...fadeInUp}>
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#87CEEB]/10 text-[#5BA3C0] rounded-full text-sm font-semibold mb-6">
                <Sparkles className="w-4 h-4" />
                Join Our Team of Amazing Support Workers
              </span>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
                Turn Your Compassion Into a
                <span className="text-[#87CEEB]"> Rewarding Career</span>
              </h1>
              
              <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
                Help NDIS participants live their best lives while earning great money with flexible hours. 
                No experience needed — just a big heart and willingness to make a difference.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Button 
                  size="lg"
                  className="bg-[#87CEEB] hover:bg-[#6BB8DB] text-white rounded-full px-10 h-14 text-lg font-semibold shadow-lg shadow-[#87CEEB]/30"
                  onClick={() => navigate("/worker-signup")}
                  data-testid="become-worker-cta"
                >
                  Start Your Journey
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </div>

              {/* Quick Stats */}
              <div className="flex flex-wrap justify-center gap-8 mt-12">
                <div className="text-center">
                  <p className="text-3xl font-bold text-slate-900">$55-80</p>
                  <p className="text-slate-500">Average hourly rate</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-slate-900">2,500+</p>
                  <p className="text-slate-500">Active workers</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-slate-900">4.9★</p>
                  <p className="text-slate-500">Worker satisfaction</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Why Support Workers Love BTR Health
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              We take care of our workers so you can focus on what you do best — helping others.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-slate-50 rounded-2xl p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className={`w-14 h-14 rounded-2xl ${benefit.color} flex items-center justify-center mb-4`}>
                  <benefit.icon className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{benefit.title}</h3>
                <p className="text-slate-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-[#87CEEB] font-semibold tracking-wide uppercase text-sm">Simple Process</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 mb-4">
              Getting Started is Easy
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              From sign up to your first client — we make it simple. Here's how it works:
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            {howItWorks.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="flex gap-6 mb-8 last:mb-0"
              >
                <div className="flex-shrink-0">
                  <div className="w-14 h-14 rounded-full bg-[#87CEEB] text-white flex items-center justify-center text-xl font-bold shadow-lg">
                    {step.step}
                  </div>
                  {index < howItWorks.length - 1 && (
                    <div className="w-0.5 h-16 bg-[#87CEEB]/30 mx-auto mt-2" />
                  )}
                </div>
                <div className="flex-1 bg-white rounded-2xl p-6 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#87CEEB]/10 flex items-center justify-center flex-shrink-0">
                      <step.icon className="w-6 h-6 text-[#87CEEB]" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 mb-2">{step.title}</h3>
                      <p className="text-slate-600">{step.description}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-[#87CEEB] to-[#6BB8DB]">
        <div className="container mx-auto px-4 md:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Make a Difference?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Join thousands of support workers who are building meaningful careers while helping others thrive.
            </p>
            <Button 
              size="lg"
              className="bg-white text-[#87CEEB] hover:bg-white/90 rounded-full px-10 h-14 text-lg font-semibold shadow-xl"
              onClick={() => navigate("/register?type=worker")}
            >
              Become a Support Worker Today
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-slate-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <p className="text-slate-400 text-sm">&copy; 2024 BTR Health. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
